// ExecutiveRiskRevenueEngine.ts
// Financial Impact Analysis — Monte Carlo Loss Modeling Engine
// Built for Board of Directors presentations and strategic decision making
import { authHeaders } from './authHeaders';
import { industryLossMultiplier } from './lossEventConstants';
import type { ExpectedLossView } from './expectedLoss';
//
// Quantitative risk model:
// - Loss Event Frequency (LEF) = factor.frequency × industry multiplier × outside pressure
// - Vulnerability = factor.currentScore (1-100, organizational susceptibility)
// - Loss Magnitude = factor.impact (revenue % at risk)
// - Data Quality = factor.confidenceLevel

export interface RiskFactor {
  id: string;
  name: string;
  category: 'governance' | 'technical_debt' | 'culture' | 'third_party' | 'budget' | 'people';
  currentScore: number; // 1-100, higher = more risk (Vulnerability)
  weight: number; // Impact multiplier
  frequency: number; // Annual probability of incident (Loss Event Frequency)
  impact: number; // Revenue percentage at risk (Loss Magnitude)
  confidenceLevel: number; // Data quality 0-1
}

export interface LossDecomposition {
  primaryLoss: {
    total: number;
    productivity: number; // Lost revenue during incident
    response: number; // Incident response costs
    replacement: number; // System replacement/rebuild
  };
  secondaryLoss: {
    total: number;
    regulatory: number; // Fines, compliance penalties
    reputation: number; // Customer/revenue churn
    legal: number; // Legal fees, notification costs
  };
}

export interface RevenueRiskProfile {
  annualRevenue: number;
  industry: string;
  companySize: string;
  riskFactors: RiskFactor[];
}

// Every money figure here is anchored to the formula ALE (revenue at risk x IRIS annual
// loss-event probability): the simulated losses are rescaled so their mean IS that ALE.
// null = not measured (no anchor, or a simulation with nothing to rescale). The simulation
// never stands in for the ALE.
export interface MonteCarloResults {
  iterations: number;
  expectedAnnualLoss: number | null; // = the formula ALE
  percentile95Loss: number | null; // Value at Risk (95th percentile), anchored
  percentile99Loss: number | null; // Extreme loss scenario, anchored
  maximumCredibleLoss: number | null; // Worst-case scenario, anchored
  lossDistribution: number[]; // Full distribution for charting (empty when not measured)
  sortedLosses: number[]; // Anchored sorted loss array for exceedance curve (empty when not measured)
  lossDecomposition: LossDecomposition | null; // Primary vs Secondary split of the ALE
  confidenceInterval: {
    lower: number;
    upper: number;
    confidence: number;
  } | null;
}

export interface BudgetOptimizationScenario {
  scenarioName: string;
  currentBudget: number;
  proposedBudget: number;
  budgetIncrease: number;
  categoryAllocations: { [category: string]: number };
  projectedRiskReduction: number;
  projectedROI: number;
  paybackPeriod: number; // months
  riskAdjustedRevenue: number;
  projectedAnnualSavings: number; // formula ALE x projectedRiskReduction
}

export interface ThreatScenario {
  id: string;
  name: string;
  description: string;
  type: 'external' | 'insider' | 'third_party' | 'operational';
  annualizedFrequency: number;
  lossMagnitudePerEvent: number;
  annualizedLossExpectancy: number | null; // shares of the formula ALE; null when not measured
  contributingCategories: string[];
  color: string;
  shape: 'circle' | 'square' | 'diamond' | 'triangle';
}

export interface ExceedancePoint {
  loss: number;
  probability: number;
}

export interface ScenarioSimulationResults {
  threatScenarios: ThreatScenario[];
  exceedanceCurve: ExceedancePoint[];
  percentileMarkers: { p50: number | null; p90: number | null; p95: number | null };
}

export interface ExecutiveSimulationResults {
  currentState: MonteCarloResults;
  scenarioResults: ScenarioSimulationResults;
  optimizedScenarios: BudgetOptimizationScenario[];
  // null when not measured, or when no scenario pays for itself at the ALE
  recommendedScenario: BudgetOptimizationScenario | null;
  boardSummary: {
    keyFindings: string[];
    recommendations: string[];
    riskTolerance: 'conservative' | 'moderate' | 'aggressive' | null; // null when not measured
    investmentPriority: string[];
  };
}

/** Decision 8: said plainly when no scenario's savings cover its cost at the formula ALE. */
export const NO_PAYBACK_MESSAGE = 'No investment scenario pays for itself at this expected annual loss.';

/** Whole dollars, e.g. $608,784. Callers pass only measured, finite figures. */
const formatDollars = (amount: number): string => {
  const rounded = Math.round(amount);
  return `${rounded < 0 ? '-' : ''}$${Math.abs(rounded).toLocaleString('en-US')}`;
};

/**
 * Executive Risk-Revenue Optimization Engine
 * Monte Carlo simulations for C-Suite strategic decision making
 */
export class ExecutiveRiskRevenueEngine {
  private readonly DEFAULT_ITERATIONS = 10000;
  // Industry loss multipliers come from IBM Cost of a Data Breach 2026 (lossEventConstants.ts,
  // a checked copy of the backend source). Until 2026-11-03 this was a hand-typed table keyed on
  // exact spellings, so 'finance' or 'Defense' on prod fell to the default.

  // NEW: Outside Pressures Enhancement Multipliers
  private readonly OUTSIDE_PRESSURE_MULTIPLIERS = {
    'Regulatory & Compliance': {
      'Healthcare': 1.3, // HIPAA, FDA regulations
      'Finance': 1.4, // SOX, PCI-DSS, banking regulations
      'Financial Services': 1.4, // Legacy support - SOX, PCI-DSS, banking regulations
      'Manufacturing': 1.1, // Industry-specific safety standards
      'Transportation and Logistics': 1.2, // DOT, FAA, maritime regulations
      'Real Estate': 1.1, // Property and zoning regulations
      'Hospitality and Tourism': 1.1, // Health and safety regulations
      'Education': 1.2, // FERPA, student privacy
      'default': 1.2
    },
    'Evolving Threat Landscape': {
      'Healthcare': 1.5, // High-value patient data
      'Finance': 1.6, // Direct financial targets
      'Financial Services': 1.6, // Legacy support - Direct financial targets
      'Technology': 1.4, // IP and trade secrets
      'Retail': 1.2, // POS and e-commerce attacks
      'Transportation and Logistics': 1.3, // Operational technology attacks
      'Hospitality and Tourism': 1.2, // Guest data breaches
      'default': 1.3
    },
    'Geopolitical Risks': {
      'Finance': 1.4, // Economic warfare targets
      'Financial Services': 1.4, // Legacy support - Economic warfare targets
      'Energy': 1.6, // Critical infrastructure targets
      'Technology': 1.3, // Tech trade wars
      'Manufacturing': 1.2, // Supply chain disruption
      'Transportation and Logistics': 1.3, // International trade impacts
      'default': 1.1
    },
    'Supply Chain Vulnerabilities': {
      'Manufacturing': 1.5, // Complex supply chains
      'Technology': 1.4, // Hardware/software dependencies
      'Healthcare': 1.2, // Medical device supply chains
      'Retail': 1.3, // Inventory supply chains
      'Transportation and Logistics': 1.6, // Core business dependency
      'Hospitality and Tourism': 1.1, // Food and service supply chains
      'default': 1.2
    },
    'Market & Economic Conditions': {
      'Finance': 1.3, // Economic sensitivity
      'Financial Services': 1.3, // Legacy support - Economic sensitivity
      'Retail': 1.2, // Consumer spending impact
      'Real Estate': 1.4, // Interest rate sensitivity
      'Hospitality and Tourism': 1.3, // Travel demand fluctuations
      'default': 1.1
    },
    'Public Perception & Reputation': {
      'Healthcare': 1.4, // Patient trust critical
      'Finance': 1.5, // Consumer confidence critical
      'Financial Services': 1.5, // Legacy support - Consumer confidence critical
      'Technology': 1.2, // Brand reputation sensitive
      'Hospitality and Tourism': 1.4, // Guest experience critical
      'Education': 1.3, // Institutional reputation
      'default': 1.1
    },
    'Technological Advancements': {
      'Technology': 1.4, // Rapid innovation cycles
      'Finance': 1.3, // Fintech disruption
      'Financial Services': 1.3, // Legacy support - Fintech disruption
      'Healthcare': 1.2, // Digital health transformation
      'Retail': 1.2, // E-commerce evolution
      'Real Estate': 1.1, // PropTech adoption
      'default': 1.2
    }
  };

  // Industry-specific named threat scenarios
  // Each scenario maps to contributing Big 6 categories and has base frequency/impact weights
  private readonly THREAT_SCENARIO_TEMPLATES: Record<string, Array<{
    id: string; name: string; description: string;
    type: 'external' | 'insider' | 'third_party' | 'operational';
    categories: string[]; freqWeight: number; impactWeight: number;
    color: string; shape: 'circle' | 'square' | 'diamond' | 'triangle';
  }>> = {
    'default': [
      { id: 'ransomware', name: 'Ransomware Attack', description: 'Encryption of critical systems via phishing or exploit', type: 'external', categories: ['technicalDebt', 'people'], freqWeight: 0.30, impactWeight: 0.35, color: '#2563eb', shape: 'circle' },
      { id: 'data_breach', name: 'Data Breach', description: 'Unauthorized access to sensitive customer or business data', type: 'external', categories: ['technicalDebt', 'governance'], freqWeight: 0.25, impactWeight: 0.30, color: '#dc2626', shape: 'square' },
      { id: 'insider_threat', name: 'Insider Threat', description: 'Privileged user data theft or sabotage', type: 'insider', categories: ['culture', 'people'], freqWeight: 0.20, impactWeight: 0.15, color: '#d97706', shape: 'diamond' },
      { id: 'vendor_breach', name: 'Third-Party Vendor Breach', description: 'Supply chain compromise through vendor access', type: 'third_party', categories: ['thirdParty', 'governance'], freqWeight: 0.15, impactWeight: 0.12, color: '#7c3aed', shape: 'triangle' },
      { id: 'compliance_failure', name: 'Compliance Failure', description: 'Regulatory penalty from audit finding or control gap', type: 'operational', categories: ['governance', 'budget'], freqWeight: 0.10, impactWeight: 0.08, color: '#059669', shape: 'circle' },
    ],
    'Healthcare': [
      { id: 'ransomware', name: 'Ransomware — Clinical Systems', description: 'Encryption of EHR/clinical systems disrupting patient care', type: 'external', categories: ['technicalDebt', 'people'], freqWeight: 0.30, impactWeight: 0.35, color: '#2563eb', shape: 'circle' },
      { id: 'phi_breach', name: 'PHI Data Breach', description: 'Protected health information exposed via unauthorized access', type: 'external', categories: ['technicalDebt', 'governance'], freqWeight: 0.25, impactWeight: 0.25, color: '#dc2626', shape: 'square' },
      { id: 'insider_phi', name: 'Insider PHI Snooping', description: 'Staff accessing patient records without authorization', type: 'insider', categories: ['culture', 'people'], freqWeight: 0.20, impactWeight: 0.10, color: '#d97706', shape: 'diamond' },
      { id: 'hipaa_penalty', name: 'HIPAA Compliance Penalty', description: 'OCR enforcement action for privacy or security rule violations', type: 'operational', categories: ['governance', 'budget'], freqWeight: 0.15, impactWeight: 0.20, color: '#7c3aed', shape: 'triangle' },
      { id: 'meddevice', name: 'Medical Device Compromise', description: 'IoT/medical device vulnerability exploited via network access', type: 'third_party', categories: ['thirdParty', 'technicalDebt'], freqWeight: 0.10, impactWeight: 0.10, color: '#059669', shape: 'circle' },
    ],
    'Finance': [
      { id: 'ransomware', name: 'Ransomware — Trading Systems', description: 'Encryption of trading or banking platforms', type: 'external', categories: ['technicalDebt', 'people'], freqWeight: 0.25, impactWeight: 0.30, color: '#2563eb', shape: 'circle' },
      { id: 'account_takeover', name: 'Account Takeover Fraud', description: 'Credential theft leading to unauthorized transactions', type: 'external', categories: ['technicalDebt', 'culture'], freqWeight: 0.25, impactWeight: 0.25, color: '#dc2626', shape: 'square' },
      { id: 'insider_fraud', name: 'Insider Trading Data Theft', description: 'Privileged access to material non-public information', type: 'insider', categories: ['culture', 'people'], freqWeight: 0.15, impactWeight: 0.20, color: '#d97706', shape: 'diamond' },
      { id: 'reg_penalty', name: 'Regulatory Enforcement Action', description: 'SEC/OCC/FINRA penalty for compliance failure', type: 'operational', categories: ['governance', 'budget'], freqWeight: 0.20, impactWeight: 0.15, color: '#7c3aed', shape: 'triangle' },
      { id: 'vendor_breach', name: 'Fintech Vendor Compromise', description: 'Third-party payment processor or API provider breach', type: 'third_party', categories: ['thirdParty', 'governance'], freqWeight: 0.15, impactWeight: 0.10, color: '#059669', shape: 'circle' },
    ],
    'Technology': [
      { id: 'ransomware', name: 'Ransomware via Supply Chain', description: 'Malware delivered through compromised software dependency', type: 'external', categories: ['technicalDebt', 'thirdParty'], freqWeight: 0.25, impactWeight: 0.30, color: '#2563eb', shape: 'circle' },
      { id: 'ip_theft', name: 'Intellectual Property Theft', description: 'Source code or trade secrets exfiltrated by APT', type: 'external', categories: ['technicalDebt', 'governance'], freqWeight: 0.20, impactWeight: 0.30, color: '#dc2626', shape: 'square' },
      { id: 'insider_ip', name: 'Departing Employee IP Theft', description: 'Engineer copying proprietary code before leaving', type: 'insider', categories: ['culture', 'people'], freqWeight: 0.25, impactWeight: 0.15, color: '#d97706', shape: 'diamond' },
      { id: 'cloud_misconfig', name: 'Cloud Misconfiguration Breach', description: 'Exposed S3 bucket, database, or API exposing customer data', type: 'operational', categories: ['technicalDebt', 'budget'], freqWeight: 0.20, impactWeight: 0.15, color: '#7c3aed', shape: 'triangle' },
      { id: 'vendor_breach', name: 'SaaS Provider Compromise', description: 'Critical SaaS vendor breach impacting operations', type: 'third_party', categories: ['thirdParty', 'governance'], freqWeight: 0.10, impactWeight: 0.10, color: '#059669', shape: 'circle' },
    ],
  };

  /**
   * Generate named threat scenarios from risk factors and industry
   */
  generateThreatScenarios(riskProfile: RevenueRiskProfile): ThreatScenario[] {
    const templates = this.THREAT_SCENARIO_TEMPLATES[riskProfile.industry] ||
                      this.THREAT_SCENARIO_TEMPLATES['default'];

    // Build lookup of risk factors by category
    const factorsByCategory: Record<string, RiskFactor> = {};
    riskProfile.riskFactors.forEach(f => { factorsByCategory[f.category] = f; });

    const industryMultiplier = industryLossMultiplier(riskProfile.industry);

    return templates.map(template => {
      // Derive frequency from contributing categories' scores and frequencies
      const contributingFactors = template.categories
        .map(c => factorsByCategory[c])
        .filter(Boolean);

      if (contributingFactors.length === 0) {
        // Fallback if categories don't match
        return {
          ...template,
          annualizedFrequency: template.freqWeight * 0.2,
          lossMagnitudePerEvent: riskProfile.annualRevenue * template.impactWeight * 0.03,
          annualizedLossExpectancy: 0,
          contributingCategories: template.categories,
        };
      }

      // Average vulnerability score from contributing categories (0-100)
      const avgVulnerability = contributingFactors.reduce((s, f) => s + f.currentScore, 0) / contributingFactors.length;
      // Average frequency from contributing categories
      const avgFrequency = contributingFactors.reduce((s, f) => s + f.frequency, 0) / contributingFactors.length;

      // Scenario frequency = base freq weighted by vulnerability and industry
      const scenarioFrequency = avgFrequency * (0.5 + avgVulnerability / 100) * industryMultiplier * template.freqWeight * 3;

      // Scenario loss magnitude = revenue × impact weight × vulnerability factor
      const impactPercent = contributingFactors.reduce((s, f) => s + f.impact, 0) / contributingFactors.length;
      const scenarioMagnitude = riskProfile.annualRevenue * (impactPercent / 100) * template.impactWeight * 3;

      const ale = scenarioFrequency * scenarioMagnitude;

      return {
        id: template.id,
        name: template.name,
        description: template.description,
        type: template.type,
        annualizedFrequency: Math.round(scenarioFrequency * 1000) / 1000,
        lossMagnitudePerEvent: Math.round(scenarioMagnitude),
        annualizedLossExpectancy: Math.round(ale),
        contributingCategories: template.categories,
        color: template.color,
        shape: template.shape,
      };
    });
  }

  // Visual mapping for major incident scenarios — color + shape by scenario type for accessibility
  private readonly SCENARIO_VISUALS: Record<string, { color: string; shape: 'circle' | 'square' | 'diamond' | 'triangle'; type: 'external' | 'insider' | 'third_party' | 'operational' }> = {
    'Ransomware Attack': { color: '#2563eb', shape: 'circle', type: 'external' },
    'Data Breach': { color: '#dc2626', shape: 'square', type: 'external' },
    'DDoS Attack': { color: '#d97706', shape: 'diamond', type: 'external' },
    'Insider Threat': { color: '#7c3aed', shape: 'triangle', type: 'insider' },
    'Supply Chain Cyber Attack': { color: '#059669', shape: 'diamond', type: 'third_party' },
    'Third-Party Vendor Breach': { color: '#059669', shape: 'diamond', type: 'third_party' },
    'Compliance Failure': { color: '#0ea5e9', shape: 'triangle', type: 'operational' },
    'OT/ICS System Attack': { color: '#ea580c', shape: 'square', type: 'operational' },
  };
  private readonly FALLBACK_VISUALS = [
    { color: '#2563eb', shape: 'circle' as const },
    { color: '#dc2626', shape: 'square' as const },
    { color: '#d97706', shape: 'diamond' as const },
    { color: '#7c3aed', shape: 'triangle' as const },
    { color: '#059669', shape: 'circle' as const },
    { color: '#0ea5e9', shape: 'square' as const },
  ];

  /**
   * Fetch major incident scenarios from the backend API
   * These are assessment-driven, calibrated to actual Big 6 scores and industry
   */
  private async fetchMajorIncidentScenarios(assessmentId: string): Promise<ThreatScenario[] | null> {
    try {
      const API_BASE = (import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001' : ''));

      // This endpoint is gated (acceptCoordinatorOrIntegration, v3.65) — anonymous requests
      // 401. See services/authHeaders.ts.
      const headers = authHeaders();

      const response = await fetch(
        `${API_BASE}/api/business-intelligence/${assessmentId}/major-incidents`,
        Object.keys(headers).length ? { headers } : undefined
      );
      if (!response.ok) {
        // Distinguish a FAILED request from "this assessment has no scenarios". The caller
        // falls back to template-generated scenarios and logs "no assessment"; without this
        // line an auth/network failure is silently reported as missing data.
        console.warn(
          `⚠️ Major Incident Scenarios request failed (${response.status}) — falling back to template scenarios, NOT because the assessment lacks data`
        );
        return null;
      }

      const data = await response.json();
      if (!data.success || !data.scenarios?.length) return null;

      console.log(`🚨 Loaded ${data.scenarios.length} Major Incident Scenarios from API`);

      return data.scenarios.map((s: any, idx: number) => {
        const visual = this.SCENARIO_VISUALS[s.name] || this.FALLBACK_VISUALS[idx % this.FALLBACK_VISUALS.length];
        const type = (this.SCENARIO_VISUALS[s.name]?.type) || 'external';

        return {
          id: `incident_${s.id}`,
          name: s.name,
          description: s.description,
          type,
          annualizedFrequency: Math.round(s.likelihood * 1000) / 1000,
          lossMagnitudePerEvent: s.most_likely_loss,
          annualizedLossExpectancy: s.annual_loss_expectancy,
          contributingCategories: (s.controls || []).map((c: any) => c.name),
          color: visual.color,
          shape: visual.shape,
        } as ThreatScenario;
      });
    } catch (error) {
      console.warn('⚠️ Failed to fetch major incidents, will use template scenarios:', error);
      return null;
    }
  }

  /**
   * Compute loss exceedance curve from sorted losses array
   * Returns points for "probability that loss exceeds X"
   */
  computeExceedanceCurve(sortedLosses: number[], bins: number = 80): ExceedancePoint[] {
    if (sortedLosses.length === 0) return [];
    const min = sortedLosses[0];
    const max = sortedLosses[sortedLosses.length - 1];
    if (max === min) return [{ loss: min, probability: 1 }];

    const points: ExceedancePoint[] = [];
    const n = sortedLosses.length;

    for (let i = 0; i <= bins; i++) {
      const lossThreshold = min + (max - min) * (i / bins);
      // Binary search for count exceeding threshold
      let lo = 0, hi = n;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (sortedLosses[mid] <= lossThreshold) lo = mid + 1;
        else hi = mid;
      }
      const exceedCount = n - lo;
      points.push({
        loss: Math.round(lossThreshold),
        probability: Math.round((exceedCount / n) * 1000) / 10, // percentage
      });
    }
    return points;
  }

  /**
   * Calculate combined outside pressure multiplier for an industry
   * Fetches real-time external risk data and applies industry-specific weighting
   */
  private async calculateOutsidePressureMultiplier(
    industry: string, 
    assessmentId?: string
  ): Promise<number> {
    try {
      // Fetch actual outside pressures data if available
      if (assessmentId) {
        const API_BASE = (import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001' : ''));
        const response = await fetch(`${API_BASE}/api/risk-intelligence/outside-pressures/${assessmentId}`, { headers: authHeaders() });
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.outsidePressures) {
            const pressuresData = data.outsidePressures;
            let combinedMultiplier = 1.0;
            
            // Apply each outside pressure category multiplier
            Object.entries(this.OUTSIDE_PRESSURE_MULTIPLIERS).forEach(([category, industryMultipliers]) => {
              const categoryScore = pressuresData.categoryScores[category];
              // A category no source measures (score null) adds no multiplier; null / 100 would
              // silently read as a measured zero.
              if (categoryScore && typeof categoryScore.score === 'number' && Number.isFinite(categoryScore.score)) {
                // Convert risk score (0-100) to multiplier (1.0-1.5)
                // Higher risk scores = higher multipliers
                const riskBasedMultiplier = 1.0 + (categoryScore.score / 100) * 0.5;
                
                // Get industry-specific multiplier for this category
                const industryMultiplier = (industryMultipliers as any)[industry] || (industryMultipliers as any)['default'];
                
                // Combine risk score with industry sensitivity
                const categoryMultiplier = riskBasedMultiplier * industryMultiplier;
                
                // Apply weighted impact (high-impact categories get more weight)
                const categoryWeight = categoryScore.criticalFactors > 0 ? 1.2 : 1.0;
                combinedMultiplier *= Math.pow(categoryMultiplier, categoryWeight / 7); // Normalize by 7 categories
              }
            });
            
            console.log(`🌍 Outside Pressures Multiplier for ${industry}: ${combinedMultiplier.toFixed(3)}`);
            console.log(`📊 Based on ${pressuresData.totalFactors} factors, ${pressuresData.criticalFactors} critical`);
            
            return Math.max(1.0, Math.min(2.0, combinedMultiplier)); // Cap between 1.0x and 2.0x
          }
        }
      }
      
      // Fallback: Use average outside pressure multipliers for industry
      console.log(`⚠️ Using fallback outside pressure multiplier for ${industry}`);
      const avgMultiplier = Object.values(this.OUTSIDE_PRESSURE_MULTIPLIERS)
        .reduce((sum, categoryMultipliers) => {
          const industryMultiplier = (categoryMultipliers as any)[industry] || (categoryMultipliers as any)['default'];
          return sum + industryMultiplier;
        }, 0) / Object.keys(this.OUTSIDE_PRESSURE_MULTIPLIERS).length;
        
      return Math.max(1.0, Math.min(1.5, avgMultiplier));
      
    } catch (error) {
      console.error('❌ Error calculating outside pressure multiplier:', error);
      return 1.1; // Conservative fallback
    }
  }

  /**
   * Run comprehensive executive simulation combining revenue risk and budget optimization
   */
  async runExecutiveSimulation(
    riskProfile: RevenueRiskProfile, 
    currentCyberBudget: number,
    budgetConstraints: { min: number; max: number },
    assessmentId: string | undefined,
    anchor: ExpectedLossView | null
  ): Promise<ExecutiveSimulationResults> {

    console.log('🏛️ Starting Executive Risk-Revenue Optimization Engine...');
    console.log(`📊 Company: ${riskProfile.industry}, Revenue: $${(riskProfile.annualRevenue / 1000000).toFixed(1)}M`);
    
    // Step 1: Calculate outside pressure multiplier
    const outsidePressureMultiplier = await this.calculateOutsidePressureMultiplier(riskProfile.industry, assessmentId);
    console.log(`🌍 Applying Outside Pressure Multiplier: ${outsidePressureMultiplier.toFixed(3)}x`);
    
    // Step 2: Enhanced risk profile with outside pressures
    const enhancedRiskProfile = {
      ...riskProfile,
      riskFactors: riskProfile.riskFactors.map(factor => ({
        ...factor,
        frequency: factor.frequency * outsidePressureMultiplier, // External pressures increase incident frequency
        impact: factor.impact * Math.min(outsidePressureMultiplier, 1.3) // Cap impact multiplier
      }))
    };
    
    // Step 3: Current state Monte Carlo analysis, anchored to the formula ALE (decision 4).
    // expectedAnnualLoss is null when not measured; the simulation never stands in for it.
    const currentState = await this.runRevenueAtRiskSimulation(
      enhancedRiskProfile,
      this.DEFAULT_ITERATIONS,
      ExecutiveRiskRevenueEngine.anchorAleOf(anchor)
    );
    const ale = currentState.expectedAnnualLoss;
    console.log(ale === null
      ? '💰 Expected annual loss: not measured'
      : `💰 Expected annual loss (formula ALE): $${Math.round(ale).toLocaleString('en-US')}`);

    // Step 4: Fetch major incident scenarios from API, fall back to templates. Either way their
    // annualized loss expectancies are shares of the formula ALE.
    const apiScenarios = assessmentId ? await this.fetchMajorIncidentScenarios(assessmentId) : null;
    const threatScenarios = apiScenarios
      ? this.anchorThreatScenarios(apiScenarios, ale, false)
      : this.anchorThreatScenarios(this.generateThreatScenarios(enhancedRiskProfile), ale, true);
    if (apiScenarios) {
      console.log('📊 Using Major Incident Scenarios from Strategic Scenarios API');
    } else {
      console.log('📊 Using template-generated threat scenarios (no assessment)');
    }
    const sorted = currentState.sortedLosses;
    const at = (q: number): number | null => (sorted.length ? sorted[Math.floor(q * sorted.length)] : null);
    const exceedanceCurve = this.computeExceedanceCurve(sorted);
    const scenarioResults: ScenarioSimulationResults = {
      threatScenarios,
      exceedanceCurve,
      percentileMarkers: { p50: at(0.50), p90: at(0.90), p95: at(0.95) }
    };
    console.log(`🎯 Generated ${threatScenarios.length} threat scenarios, ${exceedanceCurve.length} exceedance points`);

    // Step 5: Budget scenarios — only against a measured ALE (decision 2).
    const scenarios = ale === null ? [] : await this.generateBudgetScenarios(
      enhancedRiskProfile,
      currentCyberBudget,
      budgetConstraints,
      ale
    );

    // Step 6: Recommend only a scenario that pays for itself at the ALE (decision 8).
    const recommendedScenario = this.selectOptimalScenario(scenarios);

    // Step 7: Generate board-level summary and recommendations
    const boardSummary = this.generateBoardSummary(
      currentState,
      recommendedScenario,
      riskProfile
    );

    console.log('✅ Executive simulation complete');
    if (recommendedScenario) {
      console.log(`📈 Recommended budget increase: $${Math.round(recommendedScenario.budgetIncrease).toLocaleString('en-US')}`);
    } else {
      console.log('📈 No recommended investment scenario');
    }

    return {
      currentState,
      scenarioResults,
      optimizedScenarios: scenarios,
      recommendedScenario,
      boardSummary
    };
  }

  /**
   * Monte Carlo simulation for revenue-at-risk calculation
   */
  async runRevenueAtRiskSimulation(
    riskProfile: RevenueRiskProfile,
    iterations: number,
    anchorAle: number | null
  ): Promise<MonteCarloResults> {
    
    const losses: number[] = [];
    const industryMultiplier = industryLossMultiplier(riskProfile.industry);
    
    console.log(`🎲 Running ${iterations} Monte Carlo iterations...`);
    
    // Sum of weights for normalization — prevents additive compounding across categories
    const totalWeight = riskProfile.riskFactors.reduce((sum, f) => sum + f.weight, 0);

    for (let i = 0; i < iterations; i++) {
      let totalLoss = 0;

      // For each risk factor, simulate potential incidents
      riskProfile.riskFactors.forEach(factor => {
        // Sample incident occurrence (Bernoulli trial)
        const incidentOccurs = Math.random() < (factor.frequency * industryMultiplier);

        if (incidentOccurs) {
          // Sample impact severity (Beta distribution approximation)
          const impactSeverity = this.sampleBetaDistribution(
            this.scoreToAlpha(factor.currentScore),
            this.scoreToBeta(factor.currentScore)
          );

          // Calculate financial loss
          // weight is normalized to a proportion (not a raw multiplier) so categories
          // compete for their share of total loss rather than compounding independently
          const normalizedWeight = factor.weight / totalWeight;
          const factorLoss = riskProfile.annualRevenue *
                           (factor.impact / 100) *
                           impactSeverity *
                           normalizedWeight *
                           factor.confidenceLevel;

          totalLoss += factorLoss;
        }
      });

      // Cap single-year loss at 15% of revenue (even worst-case breaches rarely exceed this)
      const maxAnnualLoss = riskProfile.annualRevenue * 0.15;
      losses.push(Math.min(totalLoss, maxAnnualLoss));
    }
    
    // Anchor (decision 4): rescale every loss by k = anchorAle / simulated mean, so the mean IS
    // the formula ALE and percentiles follow while the distribution's shape is kept. Applied
    // after the 15% cap. A missing anchor, an ALE <= 0, or a simulated mean we cannot rescale (<= 0 or not
    // finite), is not measured — the raw simulation never stands in for the ALE.
    const simulatedMean = losses.length
      ? losses.reduce((sum, loss) => sum + loss, 0) / losses.length
      : NaN;
    if (anchorAle === null || !Number.isFinite(anchorAle) || anchorAle <= 0
        || !Number.isFinite(simulatedMean) || simulatedMean <= 0) {
      return {
        iterations,
        expectedAnnualLoss: null,
        percentile95Loss: null,
        percentile99Loss: null,
        maximumCredibleLoss: null,
        lossDistribution: [],
        sortedLosses: [],
        lossDecomposition: null,
        confidenceInterval: null
      };
    }
    const k = anchorAle / simulatedMean;
    for (let i = 0; i < losses.length; i++) losses[i] *= k;

    // Calculate statistics
    losses.sort((a, b) => a - b);
    const expectedLoss = anchorAle; // = mean of the anchored losses, exact rather than re-summed
    const percentile95 = losses[Math.floor(0.95 * losses.length)];
    const percentile99 = losses[Math.floor(0.99 * losses.length)];
    const maxLoss = losses[losses.length - 1];

    // Confidence interval (bootstrap method)
    const confidenceInterval = this.calculateConfidenceInterval(losses, 0.95);

    // Loss Decomposition — split ALE into primary and secondary components
    const lossDecomposition = this.calculateLossDecomposition(expectedLoss, riskProfile);

    return {
      iterations,
      expectedAnnualLoss: expectedLoss,
      percentile95Loss: percentile95,
      percentile99Loss: percentile99,
      maximumCredibleLoss: maxLoss,
      lossDistribution: this.createHistogram(losses, 50),
      sortedLosses: losses,
      lossDecomposition,
      confidenceInterval
    };
  }

  /**
   * Generate budget optimization scenarios for Board consideration
   */
  private async generateBudgetScenarios(
    riskProfile: RevenueRiskProfile,
    currentBudget: number,
    constraints: { min: number; max: number },
    baselineAle: number // the formula ALE
  ): Promise<BudgetOptimizationScenario[]> {
    
    const scenarios: BudgetOptimizationScenario[] = [];
    
    // Scenario 1: Conservative (+25% budget)
    const conservativeBudget = Math.min(currentBudget * 1.25, constraints.max);
    scenarios.push(await this.createBudgetScenario(
      'Conservative Growth',
      riskProfile,
      currentBudget,
      conservativeBudget,
      baselineAle,
      'conservative'
    ));
    
    // Scenario 2: Moderate (+50% budget) 
    const moderateBudget = Math.min(currentBudget * 1.5, constraints.max);
    scenarios.push(await this.createBudgetScenario(
      'Strategic Investment',
      riskProfile,
      currentBudget,
      moderateBudget,
      baselineAle,
      'moderate'
    ));
    
    // Scenario 3: Aggressive (+100% budget)
    const aggressiveBudget = Math.min(currentBudget * 2.0, constraints.max);
    if (aggressiveBudget <= constraints.max) {
      scenarios.push(await this.createBudgetScenario(
        'Transformational Security',
        riskProfile,
        currentBudget,
        aggressiveBudget,
        baselineAle,
        'aggressive'
      ));
    }
    
    // Scenario 4: Optimal ROI (calculated optimal point)
    const optimalBudget = this.calculateOptimalBudget(riskProfile, currentBudget, constraints);
    scenarios.push(await this.createBudgetScenario(
      'ROI Optimized',
      riskProfile,
      currentBudget,
      optimalBudget,
      baselineAle,
      'moderate'
    ));
    
    return scenarios.filter(s => s.proposedBudget >= constraints.min);
  }

  /**
   * Create individual budget scenario with risk impact modeling
   */
  private async createBudgetScenario(
    name: string,
    riskProfile: RevenueRiskProfile,
    currentBudget: number,
    proposedBudget: number,
    baselineAle: number, // the formula ALE
    riskTolerance: 'conservative' | 'moderate' | 'aggressive'
  ): Promise<BudgetOptimizationScenario> {
    
    const budgetIncrease = proposedBudget - currentBudget;
    const investmentMultiplier = budgetIncrease / currentBudget;
    
    // Calculate optimal allocation across Big 6 categories based on current risk scores
    const allocations = this.calculateOptimalAllocation(
      riskProfile.riskFactors, 
      budgetIncrease,
      riskTolerance
    );
    
    // Model risk reduction from increased investment
    const riskReduction = this.modelRiskReduction(
      riskProfile.riskFactors,
      allocations,
      investmentMultiplier
    );
    
    // Calculate financial benefits
    const projectedAnnualSavings = baselineAle * riskReduction;
    const projectedROI = projectedAnnualSavings / budgetIncrease;
    const paybackPeriod = budgetIncrease / (projectedAnnualSavings / 12);
    
    return {
      scenarioName: name,
      currentBudget,
      proposedBudget,
      budgetIncrease,
      categoryAllocations: allocations,
      projectedRiskReduction: riskReduction,
      projectedROI,
      paybackPeriod,
      riskAdjustedRevenue: riskProfile.annualRevenue - (baselineAle * (1 - riskReduction)),
      projectedAnnualSavings
    };
  }

  /**
   * Calculate optimal budget allocation across Big 6 risk categories
   */
  private calculateOptimalAllocation(
    riskFactors: RiskFactor[],
    budgetIncrease: number,
    riskTolerance: 'conservative' | 'moderate' | 'aggressive'
  ): { [category: string]: number } {
    
    // Group factors by category and calculate risk-weighted priorities
    const categoryRisks: { [category: string]: number } = {};
    const categoryWeights: { [category: string]: number } = {};
    
    riskFactors.forEach(factor => {
      if (!categoryRisks[factor.category]) {
        categoryRisks[factor.category] = 0;
        categoryWeights[factor.category] = 0;
      }
      categoryRisks[factor.category] += factor.currentScore * factor.weight * factor.impact;
      categoryWeights[factor.category] += factor.weight;
    });
    
    // Apply risk tolerance adjustments
    const toleranceMultipliers = {
      'conservative': { governance: 1.5, culture: 1.3, technical_debt: 1.0, third_party: 1.2, budget: 1.0, people: 1.4 },
      'moderate': { governance: 1.2, culture: 1.1, technical_debt: 1.3, third_party: 1.0, budget: 1.1, people: 1.2 },
      'aggressive': { governance: 1.0, culture: 1.0, technical_debt: 1.5, third_party: 1.3, budget: 1.2, people: 1.0 }
    };
    
    const multipliers = toleranceMultipliers[riskTolerance];
    
    // Calculate allocation percentages
    const totalWeightedRisk = Object.keys(categoryRisks).reduce((sum, category) => {
      const adjustedRisk = categoryRisks[category] * (multipliers[category as keyof typeof multipliers] || 1.0);
      return sum + adjustedRisk;
    }, 0);
    
    const allocations: { [category: string]: number } = {};
    Object.keys(categoryRisks).forEach(category => {
      const adjustedRisk = categoryRisks[category] * (multipliers[category as keyof typeof multipliers] || 1.0);
      allocations[category] = (adjustedRisk / totalWeightedRisk) * budgetIncrease;
    });
    
    return allocations;
  }

  /**
   * Model risk reduction from budget allocation
   * Uses exponential diminishing returns: reduction = maxReduction × (1 - e^(-k × multiplier))
   * - 25% budget increase → ~12% risk reduction
   * - 50% increase → ~22% reduction
   * - 100% increase → ~35% reduction
   * - 200% increase → ~50% reduction
   */
  private modelRiskReduction(
    riskFactors: RiskFactor[],
    allocations: { [category: string]: number },
    investmentMultiplier: number
  ): number {

    // Diminishing returns constant — controls how fast returns taper off
    const k = 0.5;
    // Maximum theoretical reduction (no investment eliminates all risk)
    const maxReduction = 0.60;

    // Base reduction from overall budget increase (proportional model)
    const baseReduction = maxReduction * (1 - Math.exp(-k * investmentMultiplier));

    // Weight the reduction by category allocation priorities
    // Categories receiving more investment get slightly better reduction
    let totalWeightedReduction = 0;
    let totalWeight = 0;

    riskFactors.forEach(factor => {
      const categoryAllocation = allocations[factor.category] || 0;
      const totalAllocation = Object.values(allocations).reduce((s, v) => s + v, 0);
      const allocationShare = totalAllocation > 0 ? categoryAllocation / totalAllocation : 1 / riskFactors.length;

      // Categories with higher allocation share get up to 30% bonus reduction
      const categoryBonus = 1.0 + (allocationShare - (1 / riskFactors.length)) * 1.5;
      const factorWeight = factor.weight * factor.impact;

      totalWeightedReduction += baseReduction * Math.max(0.7, categoryBonus) * factorWeight;
      totalWeight += factorWeight;
    });

    return totalWeight > 0 ? Math.min(totalWeightedReduction / totalWeight, maxReduction) : 0;
  }

  /**
   * Recommend the scenario that pays for itself at the formula ALE (decision 8): among scenarios
   * with budgetIncrease > 0 and projectedAnnualSavings >= budgetIncrease, the one with the
   * largest net (savings - increase). None qualifies -> null; the board summary says so plainly.
   * (Until 2026-11-03 this always returned 'ROI Optimized', which on a small ALE recommended
   * spending far more than it saved.)
   */
  private selectOptimalScenario(
    scenarios: BudgetOptimizationScenario[]
  ): BudgetOptimizationScenario | null {
    let best: BudgetOptimizationScenario | null = null;
    for (const s of scenarios) {
      if (!(s.budgetIncrease > 0) || !Number.isFinite(s.projectedAnnualSavings)) continue;
      if (s.projectedAnnualSavings < s.budgetIncrease) continue;
      if (!best || s.projectedAnnualSavings - s.budgetIncrease > best.projectedAnnualSavings - best.budgetIncrease) {
        best = s;
      }
    }
    return best;
  }

  /**
   * Generate board-level summary and recommendations. Every money figure is the anchored
   * formula ALE or derived from it; unmeasured says so and carries no dollar figure.
   */
  private generateBoardSummary(
    currentState: MonteCarloResults,
    recommended: BudgetOptimizationScenario | null,
    riskProfile: RevenueRiskProfile
  ): ExecutiveSimulationResults['boardSummary'] {
    const highestRiskCategories = this.identifyHighestRiskCategories(riskProfile.riskFactors).slice(0, 3);
    const vulnerabilityLine = highestRiskCategories.length
      ? [`Highest vulnerability areas: ${highestRiskCategories.join(', ')}`]
      : [];
    const ale = currentState.expectedAnnualLoss;

    if (ale === null) {
      return {
        keyFindings: [
          'Expected annual loss: Not measured',
          ...vulnerabilityLine
        ],
        recommendations: [
          'Investment scenarios need a measured expected annual loss, so none are evaluated.'
        ],
        riskTolerance: null,
        investmentPriority: []
      };
    }

    const revenue = riskProfile.annualRevenue;
    const p95 = currentState.percentile95Loss;
    const revenueAtRiskPercent = p95 !== null && revenue > 0 ? (p95 / revenue) * 100 : null;
    const keyFindings = [
      `Expected annual loss: ${formatDollars(ale)} across all threat scenarios`,
      ...(p95 !== null
        ? [`Value at Risk (95th percentile): ${formatDollars(p95)}${revenueAtRiskPercent !== null ? ` — ${revenueAtRiskPercent.toFixed(1)}% of annual revenue` : ''}`]
        : []),
      ...(currentState.lossDecomposition
        ? [
            `Primary losses (productivity, response, replacement): ${formatDollars(currentState.lossDecomposition.primaryLoss.total)}`,
            `Secondary losses (regulatory, reputation, legal): ${formatDollars(currentState.lossDecomposition.secondaryLoss.total)}`
          ]
        : []),
      ...vulnerabilityLine
    ];

    if (!recommended) {
      return {
        keyFindings,
        recommendations: [NO_PAYBACK_MESSAGE],
        riskTolerance: revenueAtRiskPercent !== null ? this.assessRiskTolerance(revenueAtRiskPercent) : null,
        investmentPriority: []
      };
    }

    const priorities = this.generateInvestmentPriority(recommended.categoryAllocations);
    const months = recommended.paybackPeriod;
    return {
      keyFindings,
      recommendations: [
        `Invest ${formatDollars(recommended.budgetIncrease)} to cut the ${formatDollars(ale)} expected annual loss by ${(recommended.projectedRiskReduction * 100).toFixed(0)}%`,
        ...(priorities.length ? [`Prioritize control strength in: ${priorities.slice(0, 2).join(' and ')}`] : []),
        `Expected ROI: ${(recommended.projectedROI * 100).toFixed(0)}%${Number.isFinite(months) ? ` with ${months.toFixed(1)}-month payback` : ''}`,
        `Target loss reduction: ${formatDollars(recommended.projectedAnnualSavings)} in avoided annual losses`
      ],
      riskTolerance: revenueAtRiskPercent !== null ? this.assessRiskTolerance(revenueAtRiskPercent) : null,
      investmentPriority: priorities
    };
  }

  /** The anchor's ALE when it is measured and usable; otherwise null (not measured). */
  static anchorAleOf(anchor: ExpectedLossView | null | undefined): number | null {
    if (!anchor || anchor.measured !== true) return null;
    const ale = anchor.ale;
    return typeof ale === 'number' && Number.isFinite(ale) && ale > 0 ? ale : null; // ALE <= 0: nothing to anchor (backend Task 4 ruling)
  }

  /**
   * Make threat scenarios' annualized loss expectancies shares of the formula ALE. Not measured
   * -> every ALE null. Scenarios already summing to it (the backend /major-incidents does, since
   * Task 5) are returned unchanged; otherwise they are scaled in proportion, rounded to whole
   * dollars (largest remainder) so they sum exactly. Template scenarios also scale their per-event
   * magnitude so frequency x magnitude still reads as the ALE.
   */
  private anchorThreatScenarios(
    scenarios: ThreatScenario[],
    ale: number | null,
    rescaleMagnitude: boolean
  ): ThreatScenario[] {
    if (ale === null) return scenarios.map(s => ({ ...s, annualizedLossExpectancy: null }));
    if (!scenarios.length) return scenarios;

    const values = scenarios.map(s => s.annualizedLossExpectancy);
    const allFinite = values.every((v): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0);
    const currentSum = allFinite ? (values as number[]).reduce((a, b) => a + b, 0) : NaN;
    if (allFinite && Math.abs(currentSum - ale) <= Math.max(1, scenarios.length)) return scenarios;

    // Weights: the scenarios' own ALEs; else frequency x magnitude; else an equal split.
    let weights = allFinite && currentSum > 0 ? (values as number[]) : scenarios.map(s => {
      const w = s.annualizedFrequency * s.lossMagnitudePerEvent;
      return Number.isFinite(w) && w > 0 ? w : 0;
    });
    let total = weights.reduce((a, b) => a + b, 0);
    if (!(total > 0)) { weights = scenarios.map(() => 1); total = scenarios.length; }

    // Largest-remainder rounding: whole dollars, never negative, summing exactly to the ALE.
    const target = Math.round(ale);
    const exact = weights.map(w => (w / total) * target);
    const shares = exact.map(x => Math.floor(x));
    let left = target - shares.reduce((a, b) => a + b, 0);
    const byRemainder = exact.map((_, i) => i).sort((i, j) => (exact[j] - shares[j]) - (exact[i] - shares[i]));
    for (let r = 0; left > 0; r = (r + 1) % shares.length, left--) shares[byRemainder[r]]++;

    return scenarios.map((s, i) => ({
      ...s,
      annualizedLossExpectancy: shares[i],
      lossMagnitudePerEvent: rescaleMagnitude && s.annualizedFrequency > 0
        ? Math.round(shares[i] / s.annualizedFrequency)
        : s.lossMagnitudePerEvent,
    }));
  }

  // Utility methods for statistical calculations
  private sampleBetaDistribution(alpha: number, beta: number): number {
    // Simplified beta distribution using gamma sampling
    const x = this.gammaRandom(alpha);
    const y = this.gammaRandom(beta);
    return x / (x + y);
  }

  private gammaRandom(shape: number): number {
    // Marsaglia and Tsang's method for gamma distribution
    if (shape < 1) {
      return this.gammaRandom(shape + 1) * Math.pow(Math.random(), 1 / shape);
    }
    
    const d = shape - 1/3;
    const c = 1 / Math.sqrt(9 * d);
    
    while (true) {
      let x, v;
      do {
        x = this.normalRandom();
        v = 1 + c * x;
      } while (v <= 0);
      
      v = v * v * v;
      const u = Math.random();
      
      if (u < 1 - 0.0331 * x * x * x * x) {
        return d * v;
      }
      
      if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) {
        return d * v;
      }
    }
  }

  private normalRandom(): number {
    // Box-Muller transformation
    const u = Math.random();
    const v = Math.random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  private scoreToAlpha(score: number): number {
    // Convert 1-100 risk score to beta distribution alpha parameter
    return Math.max(0.5, 5 - (score / 25)); // Higher risk = lower alpha
  }

  private scoreToBeta(score: number): number {
    // Convert 1-100 risk score to beta distribution beta parameter  
    return Math.max(0.5, 1 + (score / 50)); // Higher risk = higher beta
  }

  private calculateConfidenceInterval(data: number[], confidence: number) {
    const sorted = [...data].sort((a, b) => a - b);
    const alpha = 1 - confidence;
    const lowerIndex = Math.floor(alpha / 2 * sorted.length);
    const upperIndex = Math.floor((1 - alpha / 2) * sorted.length);
    
    return {
      lower: sorted[lowerIndex],
      upper: sorted[upperIndex],
      confidence
    };
  }

  private createHistogram(data: number[], bins: number): number[] {
    const min = Math.min(...data);
    const max = Math.max(...data);
    const binWidth = (max - min) / bins;
    const histogram = new Array(bins).fill(0);
    
    data.forEach(value => {
      const binIndex = Math.min(Math.floor((value - min) / binWidth), bins - 1);
      histogram[binIndex]++;
    });
    
    return histogram;
  }

  /**
   * Loss Decomposition
   * Splits total ALE into Primary Loss (direct) and Secondary Loss (indirect)
   * Ratios are industry-calibrated based on Ponemon/IBM breach cost studies
   */
  private calculateLossDecomposition(
    expectedAnnualLoss: number,
    riskProfile: RevenueRiskProfile
  ): LossDecomposition {
    // Industry-specific primary/secondary split ratios
    // Source: IBM Cost of a Data Breach Report patterns
    const industrySplits: { [key: string]: { primary: number; secondary: number } } = {
      'Healthcare': { primary: 0.40, secondary: 0.60 }, // Heavy regulatory/reputation
      'Finance': { primary: 0.35, secondary: 0.65 }, // Major regulatory exposure
      'Financial Services': { primary: 0.35, secondary: 0.65 },
      'Technology': { primary: 0.50, secondary: 0.50 }, // Balanced
      'Retail': { primary: 0.45, secondary: 0.55 }, // Customer churn impact
      'Manufacturing': { primary: 0.55, secondary: 0.45 }, // More direct operational loss
      'Energy': { primary: 0.45, secondary: 0.55 },
      'Education': { primary: 0.55, secondary: 0.45 },
      'default': { primary: 0.45, secondary: 0.55 }
    };

    const split = industrySplits[riskProfile.industry] || industrySplits['default'];
    const primaryTotal = expectedAnnualLoss * split.primary;
    const secondaryTotal = expectedAnnualLoss * split.secondary;

    return {
      primaryLoss: {
        total: primaryTotal,
        productivity: primaryTotal * 0.45, // Lost business during downtime
        response: primaryTotal * 0.35, // Incident response, forensics, remediation
        replacement: primaryTotal * 0.20 // System rebuild, data recovery
      },
      secondaryLoss: {
        total: secondaryTotal,
        regulatory: secondaryTotal * 0.30, // Fines and compliance penalties
        reputation: secondaryTotal * 0.45, // Customer churn, brand damage
        legal: secondaryTotal * 0.25 // Legal counsel, notification, settlements
      }
    };
  }

  private calculateOptimalBudget(
    riskProfile: RevenueRiskProfile,
    currentBudget: number,
    constraints: { min: number; max: number }
  ): number {
    // USE SAME FORMULA AS BACKEND (business-intelligence.js calculatePreciseInvestment)
    // This ensures consistency between Investment Scenarios and Monte Carlo Board Reports

    // Base calculation: industry benchmark suggests 3.5% of revenue
    const industryBenchmark = riskProfile.annualRevenue * 0.035;

    // Current budget may be under-invested
    const investmentGap = Math.max(0, industryBenchmark - currentBudget);

    // Additional investment needed for 30% risk reduction
    // Each 10% risk reduction requires approximately 15-20% more investment
    const riskReductionTarget = 0.3; // 30% risk reduction (matches backend)
    const riskReductionMultiplier = 1 + (riskReductionTarget * 1.5);

    // Calculate target investment
    const targetInvestment = currentBudget * riskReductionMultiplier + (investmentGap * riskReductionTarget);

    // Ensure minimum meaningful increase (at least 25% increase for optimization)
    const minIncrease = currentBudget * 0.25;
    const optimizedInvestment = Math.max(targetInvestment, currentBudget + minIncrease);

    return Math.max(constraints.min, Math.min(constraints.max, optimizedInvestment));
  }

  private calculateOverallRiskScore(riskFactors: RiskFactor[]): number {
    const totalWeightedRisk = riskFactors.reduce((sum, factor) => 
      sum + (factor.currentScore * factor.weight * factor.impact), 0
    );
    const totalWeight = riskFactors.reduce((sum, factor) => sum + factor.weight, 0);
    return totalWeight > 0 ? totalWeightedRisk / totalWeight : 50;
  }

  private identifyHighestRiskCategories(riskFactors: RiskFactor[]): string[] {
    const categoryRisks: { [category: string]: number } = {};
    
    riskFactors.forEach(factor => {
      if (!categoryRisks[factor.category]) {
        categoryRisks[factor.category] = 0;
      }
      categoryRisks[factor.category] += factor.currentScore * factor.weight * factor.impact;
    });
    
    return Object.entries(categoryRisks)
      .sort(([,a], [,b]) => b - a)
      .map(([category]) => category.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, l => l.toUpperCase()));
  }

  private assessRiskTolerance(revenueAtRiskPercent: number): 'conservative' | 'moderate' | 'aggressive' {
    // High exposure = aggressive (taking on more risk), low = conservative (well-protected)
    if (revenueAtRiskPercent > 15) return 'aggressive';
    if (revenueAtRiskPercent > 8) return 'moderate';
    return 'conservative';
  }

  private generateInvestmentPriority(allocations: { [category: string]: number }): string[] {
    return Object.entries(allocations)
      .sort(([,a], [,b]) => b - a)
      .map(([category]) => category.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, l => l.toUpperCase()));
  }
}

// Export singleton instance
export const executiveRiskEngine = new ExecutiveRiskRevenueEngine();